import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { Membresia, Operaciones, ParDeTokens } from '../api/operaciones';
import { RUTAS_ACCESO, RUTAS_ALTA_CON_SESION, RUTA_BACKOFFICE, RUTA_PORTAL_PARTNER, esRutaAcceso, esRutaLibre, useRuta } from '../rutas';
import type { AlmacenSesion, Sesion } from '../sesion/sesion';
import { MENSAJES, faltaVerificarEmail, faltanDosPasos, negocioAlEntrar, pantallaDeRuta, saludo, type PantallaAcceso } from './acceso';

/**
 * Estado de la sesión del panel y adónde lleva cada uno, sin nada visual (lo usa el marco nuevo, G127, ADR-0050):
 * sin sesión, las rutas del acceso; con sesión y sin negocio, elegirlo (1.4: con uno solo se entra directamente); con
 * negocio, el panel.
 */
export interface EleccionNegocio {
    saludo: string;
    negocios: readonly Membresia[];
    /** Texto en lugar de la lista (sin negocio o cuenta de otro tipo, P0-11). */
    aviso: string | null;
    elegir: (id: string) => Promise<void>;
    /** Cerrar esta sesión para entrar con otra cuenta (cuando hay aviso en lugar de negocios). */
    salir: () => void;
}

export interface SesionPanel {
    sesion: Sesion | null;
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (destino: string, reemplazar?: boolean) => void;
    /** Pantalla del acceso que toca (sin sesión o sin negocio); null con negocio. */
    pantallaAcceso: PantallaAcceso | null;
    /** Datos de 1.4 cuando ya hay sesión sin negocio; null mientras se cargan. */
    eleccion: EleccionNegocio | null;
    /** Tras createSession o el segundo paso: guarda la sesión y va a elegir negocio o al panel. */
    alEntrar: (tokens: ParDeTokens) => void;
    elegir: (id: string) => Promise<void>;
    salir: () => void;
    /** Cierra la sesión en la API (si puede) y vuelve a entrar. */
    cerrarSesion: () => void;
    /** Lleva a «Activa la verificación en dos pasos» (Soporte o Superadmin sin ellos, `two_factor_required`). */
    faltanLosDosPasos: () => void;
}

export function useSesionPanel(ops: Operaciones, almacen: AlmacenSesion, rutaServidor = '/'): SesionPanel {
    const suscribir = useCallback((aviso: () => void) => almacen.escuchar(aviso), [almacen]);
    const leerSesion = useCallback(() => almacen.actual(), [almacen]);
    const sesion = useSyncExternalStore(suscribir, leerSesion, leerSesion);
    const { ruta, busqueda, navegar } = useRuta(rutaServidor);
    const [eleccion, setEleccion] = useState<EleccionNegocio | null>(null);

    const salir = useCallback(() => {
        setEleccion(null);
        almacen.borrar();
        navegar(RUTAS_ACCESO.entrar, true);
    }, [almacen, navegar]);

    /** Soporte o Superadmin sin los dos pasos (G65): `403 two_factor_required` lleva a activarlos (G123). */
    const faltanLosDosPasos = useCallback(() => {
        navegar(RUTAS_ACCESO.activarDosPasos, true);
    }, [navegar]);

    const elegir = useCallback(
        async (id: string) => {
            try {
                const tokens = await ops.selectBusiness(id);
                almacen.guardar(tokens);
                navegar('/', true);
            } catch (error) {
                if (faltanDosPasos(error)) {
                    faltanLosDosPasos();
                    return;
                }
                if (faltaVerificarEmail(error)) {
                    navegar(RUTAS_ACCESO.verificarEmail, true);
                    return;
                }
                throw error;
            }
        },
        [ops, almacen, navegar, faltanLosDosPasos],
    );

    // Cada estado de la sesión tiene sus rutas: sin sesión, el acceso; sin negocio, elegirlo; con negocio, el panel. La de
    // activar los dos pasos vale con sesión (con negocio o sin él): es adonde lleva `two_factor_required`. La de verificar
    // el email (1.2) vale siempre: el enlace del correo se abre con sesión o sin ella.
    const sinNegocio = sesion !== null && sesion.negocio === null;
    const activar = ruta === RUTAS_ACCESO.activarDosPasos;
    const verificar = ruta === RUTAS_ACCESO.verificarEmail;
    // «Protege tu cuenta» (1.6) y «Tu negocio» (1.5) del alta (tarea 490): con sesión y sin negocio, como elegir negocio.
    const enAlta = RUTAS_ALTA_CON_SESION.includes(ruta);
    // El portal de partners (1.23) va con sesión y sin negocio: lo pinta App con su propio marco.
    // El backoffice (1.20) también: sesión de una cuenta de plataforma sin negocio fijado, con su propio marco.
    const enPortal = ruta === RUTA_PORTAL_PARTNER || ruta === RUTA_BACKOFFICE;
    useEffect(() => {
        if (verificar || esRutaLibre(ruta)) {
            return;
        }
        if (sesion === null && (!esRutaAcceso(ruta) || ruta === RUTAS_ACCESO.elegirNegocio || activar || enAlta)) {
            navegar(RUTAS_ACCESO.entrar, true);
        } else if (sinNegocio && ruta !== RUTAS_ACCESO.elegirNegocio && !activar && !enAlta && !enPortal) {
            navegar(RUTAS_ACCESO.elegirNegocio, true);
        } else if (sesion !== null && !sinNegocio && (esRutaAcceso(ruta) || enPortal) && !activar) {
            navegar('/', true);
        }
    }, [sesion, sinNegocio, ruta, activar, verificar, enAlta, enPortal, navegar]);

    // 1.4: con sesión y sin negocio, se cargan los negocios de la cuenta; con uno solo, se entra directamente. Sin el email
    // verificado no se entra en ningún negocio (G66): «Revisa tu correo» (1.2).
    useEffect(() => {
        // En las rutas libres (aceptar una invitación, la tablet) la pantalla decide adónde va: no se elige negocio solo.
        if (!sinNegocio || activar || verificar || enPortal || esRutaLibre(ruta)) {
            return;
        }
        let vivo = true;
        ops.getMe()
            .then((yo) => {
                if (!vivo) {
                    return;
                }
                // La cuenta de partner también verifica su email antes de entrar en su portal (G66, E7).
                if ((yo.account.type === 'staff' || yo.account.type === 'partner') && !yo.account.email_verified) {
                    navegar(RUTAS_ACCESO.verificarEmail, true);
                    return;
                }
                // Un solo inicio de sesión (G133): la cuenta de partner va a su portal (1.23).
                if (yo.account.type === 'partner') {
                    navegar(RUTA_PORTAL_PARTNER, true);
                    return;
                }
                // …y la de Soporte o Superadmin, al backoffice (1.20); sin los dos pasos, antes los activa (G65, G123), y sin el
                // email verificado, antes lo verifica (G66): la API responde `email_not_verified` a todo lo del backoffice.
                if (yo.account.type === 'platform') {
                    if (!yo.account.two_factor_enabled) {
                        faltanLosDosPasos();
                        return;
                    }
                    if (!yo.account.email_verified) {
                        navegar(RUTAS_ACCESO.verificarEmail, true);
                        return;
                    }
                    navegar(RUTA_BACKOFFICE, true);
                    return;
                }
                const decision = negocioAlEntrar(yo);
                const base = { saludo: saludo(yo), negocios: [], aviso: null, elegir, salir };
                switch (decision.tipo) {
                    case 'directo':
                        // En el alta no se entra solo en un negocio: lo hace «Tu negocio» al crearlo.
                        if (enAlta) {
                            setEleccion({ ...base, negocios: [decision.negocio] });
                            break;
                        }
                        setEleccion(base);
                        void elegir(decision.negocio.business_id).catch(() => {
                            setEleccion({ ...base, negocios: [decision.negocio] });
                        });
                        break;
                    case 'elegir':
                        setEleccion({ ...base, negocios: decision.negocios });
                        break;
                    case 'ninguno':
                        setEleccion({ ...base, aviso: MENSAJES.sinNegocio });
                        break;
                    case 'otra-cuenta':
                        setEleccion({ ...base, aviso: MENSAJES.otraCuenta });
                        break;
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (faltanDosPasos(error)) {
                    faltanLosDosPasos();
                } else {
                    salir();
                }
            });
        return () => {
            vivo = false;
        };
    }, [sinNegocio, activar, verificar, enAlta, enPortal, ruta, ops, elegir, salir, faltanLosDosPasos, navegar]);

    const alEntrar = useCallback(
        (tokens: ParDeTokens) => {
            almacen.guardar(tokens);
            navegar(tokens.business_id === null ? RUTAS_ACCESO.elegirNegocio : '/', true);
        },
        [almacen, navegar],
    );

    const cerrarSesion = useCallback(() => {
        ops.deleteCurrentSession()
            .catch(() => undefined)
            .finally(salir);
    }, [ops, salir]);

    const pantallaAcceso: PantallaAcceso | null =
        sesion === null ? pantallaDeRuta(ruta) : activar ? 'force2fa' : verificar ? 'verify' : sinNegocio && !enPortal ? (enAlta ? pantallaDeRuta(ruta) : 'biz') : null;

    return {
        sesion,
        ruta,
        busqueda,
        navegar,
        pantallaAcceso,
        eleccion: sinNegocio ? eleccion : null,
        alEntrar,
        elegir,
        salir,
        cerrarSesion,
        faltanLosDosPasos,
    };
}
